import React, { useMemo, useState } from 'react';
import type { CommunitySidebarHeader, SectionStatus } from './types';
import { useCommunityForum } from './CommunityForumContext';
import { CommunityDevField, CommunityDevRowMeta, CommunityDevTh } from './CommunityDevUi';
import { entityRef, P } from './communityDevMode';
import type { HeaderInput } from './communityForumStore';

function emptyHeader(sortOrder: number): HeaderInput {
  return { title: '', sortOrder, status: 'active' };
}

function headerToForm(header: CommunitySidebarHeader): HeaderInput {
  return { title: header.title, sortOrder: header.sortOrder, status: header.status };
}

export default function CommunityAdminHeaders() {
  const { state, addHeader, editHeader, removeHeader, moveHeaders } = useCommunityForum();
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<HeaderInput>(() => emptyHeader(state.headers.length + 1));

  const headers = useMemo(
    () => [...state.headers].sort((a, b) => a.sortOrder - b.sortOrder),
    [state.headers],
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyHeader(state.headers.length + 1));
    setFormOpen(true);
  };

  const openEdit = (header: CommunitySidebarHeader) => {
    setEditingId(header.id);
    setForm(headerToForm(header));
    setFormOpen(true);
  };

  const saveForm = () => {
    if (!form.title.trim()) return;
    if (editingId) editHeader(editingId, form);
    else addHeader(form);
    setFormOpen(false);
  };

  const moveUp = (id: string) => {
    const ordered = headers.map((h) => h.id);
    const idx = ordered.indexOf(id);
    if (idx <= 0) return;
    [ordered[idx - 1], ordered[idx]] = [ordered[idx], ordered[idx - 1]];
    moveHeaders(ordered);
  };

  const moveDown = (id: string) => {
    const ordered = headers.map((h) => h.id);
    const idx = ordered.indexOf(id);
    if (idx < 0 || idx >= ordered.length - 1) return;
    [ordered[idx], ordered[idx + 1]] = [ordered[idx + 1], ordered[idx]];
    moveHeaders(ordered);
  };

  return (
    <div className="mt-6">
      <div className="dcf-toolbar">
        <button type="button" className="dcf-btn dcf-btn--primary" onClick={openCreate}>
          <i className="fas fa-plus" aria-hidden="true" />
          Создать заголовок
        </button>
      </div>

      <div className="dcf-admin-table-wrap mt-4">
        <table className="dcf-admin-table">
          <thead>
            <tr>
              <CommunityDevTh label="Заголовок" storagePath={P.headerTitle} />
              <CommunityDevTh label="Статус" storagePath={P.headerStatus} />
              <CommunityDevTh label="Порядок" storagePath={P.headerSortOrder} />
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {headers.map((header) => (
              <tr key={header.id}>
                <td className="font-semibold text-slate-800">
                  {header.title}
                  <CommunityDevRowMeta parts={[entityRef(P.headers, header.id)]} />
                </td>
                <td>
                  <span className={`dcf-status-pill ${header.status === 'active' ? 'dcf-status-pill--active' : 'dcf-status-pill--hidden'}`}>
                    {header.status === 'active' ? 'Активен' : 'Скрыт'}
                  </span>
                </td>
                <td>
                  <div className="flex gap-1">
                    <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={() => moveUp(header.id)}>↑</button>
                    <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={() => moveDown(header.id)}>↓</button>
                  </div>
                </td>
                <td>
                  <div className="flex flex-wrap gap-1">
                    <button type="button" className="dcf-btn dcf-btn--ghost text-xs" onClick={() => openEdit(header)}>Редактировать</button>
                    <button
                      type="button"
                      className="dcf-btn dcf-btn--ghost text-xs"
                      onClick={() => editHeader(header.id, { status: header.status === 'active' ? 'hidden' : 'active' })}
                    >
                      {header.status === 'active' ? 'Скрыть' : 'Показать'}
                    </button>
                    <button
                      type="button"
                      className="dcf-btn dcf-btn--ghost dcf-btn--danger text-xs"
                      onClick={() => {
                        try {
                          if (window.confirm(`Удалить заголовок «${header.title}»?`)) removeHeader(header.id);
                        } catch {
                          /* alert in context */
                        }
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {formOpen && (
        <div className="dcf-modal-overlay" onClick={() => setFormOpen(false)} role="presentation">
          <div className="dcf-modal" onClick={(e) => e.stopPropagation()} role="dialog">
            <h2 className="dcf-modal__title">{editingId ? 'Редактировать заголовок' : 'Создать заголовок'}</h2>
            <CommunityDevField label="Текст в левой панели" storagePath={P.headerTitle}>
              <input
                className="dcf-input"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              />
            </CommunityDevField>
            <CommunityDevField label="Порядок" storagePath={P.headerSortOrder}>
              <input
                className="dcf-input"
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm((p) => ({ ...p, sortOrder: Number(e.target.value) || 1 }))}
              />
            </CommunityDevField>
            <CommunityDevField label="Статус" storagePath={P.headerStatus}>
              <select
                className="dcf-select"
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as SectionStatus }))}
              >
                <option value="active">Активен</option>
                <option value="hidden">Скрыт</option>
              </select>
            </CommunityDevField>
            <div className="dcf-modal__actions">
              <button type="button" className="dcf-btn dcf-btn--secondary" onClick={() => setFormOpen(false)}>Отмена</button>
              <button type="button" className="dcf-btn dcf-btn--primary" onClick={saveForm}>Сохранить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
